/**
 * Shared state for hint-row selection.
 *
 * Unified model: one selection at a time (not per-lane), since chords
 * span multiple lanes/hands. The selection stores all MIDI notes across
 * all hands so that highlighting works for any lane in the piano roll.
 */
export class HintSelection {
  private _midis: Set<number> = new Set();
  private _tick = -1;
  private _maxOnsetTick = -1;
  private _active = false;

  private _autoSelect = true;
  private _version = 0;
  private _listeners: (() => void)[] = [];

  get hasSelection(): boolean {
    return this._active;
  }

  get autoSelect(): boolean {
    return this._autoSelect;
  }

  /** Monotonically increasing counter, bumped on every mutation. */
  get version(): number {
    return this._version;
  }

  setAutoSelect(enabled: boolean): void {
    if (this._autoSelect === enabled) {
return;
}

    this._autoSelect = enabled;
    if (!enabled) {
      this.clear();
    }
  }

  /**
   * Set the current selection (used by auto-select, no toggle).
   * All MIDI notes across all hands are included.
   */
  set(midis: number[], tick: number, maxOnsetTick: number): void {
    // Skip when nothing actually changes. Comparing only `tick` was a
    // false optimization: callers that drive `maxOnsetTick` or `midis`
    // independently (e.g. the learning journey, where the highlight
    // window grows with sustained notes) would have their updates
    // silently dropped.
    if (
      this._active &&
      this._tick === tick &&
      this._maxOnsetTick === maxOnsetTick &&
      sameMembers(this._midis, midis)
    ) {
      return;
    }

    this._midis = new Set(midis);
    this._tick = tick;
    this._maxOnsetTick = maxOnsetTick;
    this._active = true;
    this._version++;
  }

  /**
   * Manual select (toggle): tapping the same row deselects.
   */
  select(midis: number[], tick: number, maxOnsetTick: number): void {
    if (this._active && this._tick === tick) {
      this.clear();
    } else {
      this._midis = new Set(midis);
      this._tick = tick;
      this._maxOnsetTick = maxOnsetTick;
      this._active = true;
      this._version++;
      this.notify();
    }
  }

  /**
   * Check whether a note should be highlighted in the piano roll.
   * Works across all lanes, the laneIdx parameter is accepted for API
   * compatibility but is not used for filtering (the chord is unified).
   */
  isHighlighted(_laneIdx: number, midi: number, noteTick: number): boolean {
    if (!this._active) {
return false;
}

    return this._midis.has(midi) && noteTick >= this._tick && noteTick <= this._maxOnsetTick;
  }

  /** Check whether a hint row at the given tick is selected. */
  isRowSelected(tick: number): boolean {
    return this._active && this._tick === tick;
  }

  clear(): void {
    if (!this._active) {
return;
}

    this._active = false;
    this._midis.clear();
    this._tick = -1;
    this._maxOnsetTick = -1;
    this._version++;
    this.notify();
  }

  onChange(fn: () => void): () => void {
    this._listeners.push(fn);

    return () => {
      this._listeners = this._listeners.filter(l => l !== fn);
    };
  }

  private notify(): void {
    for (const fn of this._listeners) {
fn();
}
  }
}

function sameMembers(a: ReadonlySet<number>, b: readonly number[]): boolean {
  if (a.size !== b.length) {
    return false;
  }

  for (const v of b) {
    if (!a.has(v)) {
      return false;
    }
  }

  return true;
}
